import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import StepContent from "@material-ui/core/StepContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import HelpIcon from "@material-ui/icons/Help";
import Tooltip from "@material-ui/core/Tooltip";

import LoadingButton from "../../Buttons/Loading";
import * as constants from "../../../constants";
import * as actions from "../../../store/actions";
import {
  createServiceProviderUser,
  batchWriteDivisionRelations,
} from "../../../utils/appsyncOperations";
import { isUserRole } from "../../../utils/user";
import HelpAdornment from "../../InputAdornments/Help";
import DivisionSelector from "../../DivisionSelector";

const CreatePropertyStepper = ({ isOpen, onClose }) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [nextButtonLoading, setNextButtonLoading] = useState(false);
  const [divisionIds, setDivisionIds] = useState([]);
  const serviceProviderId = useSelector(
    (state) => state.serviceProvider.serviceProvider.serviceProviderId
  );
  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const dispatch = useDispatch();
  const history = useHistory();
  const steps = [
    "General",
    <span className={classes.stepperHelpContainer}>
      Divisions
      <Tooltip title={constants.helpText.userDivisionAdd}>
        <HelpIcon
          className={classes.stepperHelp}
          color={"action"}
          fontSize={"small"}
        />
      </Tooltip>
    </span>,
  ];

  const generalForm = useFormik({
    initialValues: {
      displayName: "",
      email: "",
      role: constants.user.role.admin.value,
    },
    onSubmit: (values) => generalFormSubmit(values),
    validationSchema: yup.object({
      displayName: yup
        .string()
        .max(140, "Must be 140 characters or less.")
        .required("Required"),
      email: yup
        .string()
        .email("Email must be a valid email")
        .required("Required"),
      role: yup
        .string()
        .oneOf(Object.values(constants.user.role).map((role) => role.value))
        .required("Required"),
    }),
  });

  const generalFormSubmit = () => {
    handleNext();
  };

  const createUser = async () => {
    if (divisionIds.length === 0)
      return enqueueSnackbar(constants.errors.noDivisionsOnProperty, {
        variant: "error",
      });
    setNextButtonLoading(true);
    const input = {
      email: generalForm.values.email,
      displayName: generalForm.values.displayName,
      serviceProviderId,
      role: generalForm.values.role,
    };

    try {
      const user = await createServiceProviderUser(input);
      const { userId } = user;
      const divisionsInput = divisionIds.map((id) => ({
        divisionId: id,
        relationalId: userId,
        operation: "CREATE",
        variant: "USER",
        serviceProviderId,
      }));
      const divisionRelations = await batchWriteDivisionRelations(
        divisionsInput
      );
      setNextButtonLoading(false);
      dispatch(actions.users.setUser(user));
      dispatch(actions.divisions.setDivisions({ divisionRelations }));
      history.push(`/map/users/${userId}`);
    } catch (e) {
      enqueueSnackbar(e, { variant: "error" });
      setNextButtonLoading(false);
    }
  };

  const getNextHandler = () => {
    switch (activeStep) {
      case 0:
        return generalForm.submitForm();
      case 1:
        return createUser();
      default:
        return () => {};
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderStepButtons = () => {
    return (
      <div className={classes.actionsContainer}>
        <>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            className={classes.button}
          >
            Back
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={getNextHandler}
            className={classes.button}
            isLoading={nextButtonLoading}
          >
            {activeStep === steps.length - 1 ? "Finish" : "Next"}
          </LoadingButton>
        </>
      </div>
    );
  };

  const renderGeneralInfo = () => {
    const displayNameError =
      generalForm.touched.displayName && generalForm.errors.displayName;
    const emailError = generalForm.touched.email && generalForm.errors.email;
    const roleError = generalForm.touched.role && generalForm.errors.role;
    return (
      <>
        <Grid container spacing={2} justify="space-between">
          <Grid item xs={12} sm={4}>
            <TextField
              id={"displayName"}
              label="Display Name"
              onBlur={generalForm.onBlur}
              value={generalForm.values.displayName}
              onChange={generalForm.handleChange}
              required={true}
              variant="filled"
              fullWidth
              InputProps={{
                startAdornment: (
                  <HelpAdornment title={constants.helpText.userDisplayName} />
                ),
              }}
              error={displayNameError}
              helperText={
                displayNameError ? generalForm.errors.displayName : null
              }
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              id={"email"}
              label="Email"
              onBlur={generalForm.onBlur}
              value={generalForm.values.email}
              onChange={generalForm.handleChange}
              required={true}
              variant="filled"
              fullWidth
              InputProps={{
                startAdornment: (
                  <HelpAdornment title={constants.helpText.userEmail} />
                ),
              }}
              error={emailError}
              helperText={emailError ? generalForm.errors.email : null}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              id={"role"}
              label="Role"
              onBlur={generalForm.onBlur}
              value={generalForm.values.role}
              onChange={(e) =>
                generalForm.setFieldValue("role", e.target.value)
              }
              fullWidth
              variant="filled"
              InputProps={{
                startAdornment: (
                  <HelpAdornment title={constants.helpText.userRole} />
                ),
              }}
              error={roleError}
              helperText={roleError ? generalForm.errors.role : null}
            >
              {Object.values(constants.user.role).map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.text}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        {renderStepButtons()}
      </>
    );
  };

  const renderDivisions = () => {
    return (
      <List>
        <ListSubheader>
          {constants.helpText.userDivisionSelection}
        </ListSubheader>
        <ListItem>
          <DivisionSelector
            selected={divisionIds}
            onSelectionChange={(ids) => setDivisionIds(ids)}
          />
        </ListItem>
        <ListItem>{renderStepButtons()}</ListItem>
      </List>
    );
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderGeneralInfo();
      case 1:
        return renderDivisions();
      default:
        return "Unknown step";
    }
  };

  const isAdmin = isUserRole({ admin: true });

  if (!isAdmin)
    return (
      <>
        <Dialog open={isOpen} onClose={onClose} maxWidth={"md"} fullWidth>
          <DialogTitle>Create user</DialogTitle>
          <DialogContent>
            <DialogContentText>You are not the correct role.</DialogContentText>
          </DialogContent>
        </Dialog>
      </>
    );

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth={"md"} fullWidth>
      <DialogTitle>Create User</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} orientation={"vertical"}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>{getStepContent(index)}</StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  stepperHelpContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "flex-start",
    alignItems: "center",
  },
  stepperHelp: {
    marginLeft: theme.spacing(1),
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
  },
  resetContainer: {
    padding: theme.spacing(3),
  },
  divisionSelectionText: {
    marginBottom: theme.spacing(1),
  },
}));

export default CreatePropertyStepper;
